/**
 * useIcalFeed — get or lazily generate the ical_token for the current user's business profile.
 *
 * Token generation happens here in the hook, not in the calendar-feed edge function.
 * The edge function only validates an existing token and serves the feed.
 *
 * Pre-launch: no profile exists yet, so this returns null until after Launch (step 7).
 * Post-launch / dashboard: fetches the token from business_profiles, generating one if missing.
 *
 * The feed URL is:
 *   https://<project>.supabase.co/functions/v1/calendar-feed?token=<ical_token>
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const FEED_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-feed`;

interface IcalFeedState {
  feedUrl: string | null;
  loading: boolean;
  /** Call to rotate the token and invalidate existing subscriptions. Throws on failure. */
  rotate: () => Promise<void>;
}

export function useIcalFeed(): IcalFeedState {
  const { user } = useAuth();
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function fetchOrGenerate() {
    setLoading(true);
    try {
      const { data: rows } = await supabase
        .from('business_profiles')
        .select('id, ical_token')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const profile = rows?.[0] ?? null;

      if (!profile) {
        // Pre-launch — no profile yet
        setFeedUrl(null);
        return;
      }

      let token = profile.ical_token as string | null;

      if (!token) {
        // Generate a new UUID token and persist it
        const newToken = crypto.randomUUID();
        const { error } = await supabase
          .from('business_profiles')
          .update({ ical_token: newToken })
          .eq('id', profile.id);
        if (!error) token = newToken;
      }

      setFeedUrl(token ? `${FEED_BASE}?token=${token}` : null);
    } catch {
      // Non-blocking
    } finally {
      setLoading(false);
    }
  }

  async function rotate() {
    if (!user) return;
    setLoading(true);
    try {
      const { data: rows } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);
      const profile = rows?.[0] ?? null;
      if (!profile) return;
      const newToken = crypto.randomUUID();
      const { error } = await supabase
        .from('business_profiles')
        .update({ ical_token: newToken })
        .eq('id', profile.id);
      if (error) throw error;
      setFeedUrl(`${FEED_BASE}?token=${newToken}`);
    } finally {
      setLoading(false);
    }
  }

  return { feedUrl, loading, rotate };
}
